/**
 * Деривация аудиторных мульти-атрибутов «Персона» и «Статус получателя»
 * из пары «Пол аудитории» × «Возраст аудитории».
 *
 * Схема снята с боевого каталога (18 813 макетов, срез 06.08.2026): связки
 * design_personas / design_recipient_statuses по gender+age дают устойчивые
 * доминанты, они и зафиксированы здесь. Модуль — чистый: только справочные
 * строки, никаких запросов. Значения выдаются в порядке attribute_values.sort,
 * чтобы UI и БД показывали одинаковую последовательность.
 *
 * Результат — ПРЕДЗАПОЛНЕНИЕ: пользователь вправе изменить набор в визарде.
 */

export const GENDER_MALE = 'Мужской'
export const GENDER_FEMALE = 'Женский'
export const GENDER_UNISEX = 'Унисекс'

export const AGE_BABY = 'Малыш'
export const AGE_CHILD = 'Ребёнок'
export const AGE_TEEN = 'Подросток'
export const AGE_ADULT = 'Взрослый (18+)'
export const AGE_SENIOR = 'Пожилой'
export const AGE_UNIVERSAL = 'Универсальный'

/** Порядок значений справочника persona (attribute_values.sort). */
const PERSONA_ORDER = ['Мальчик', 'Девочка', 'Парень', 'Девушка', 'Мужчина', 'Женщина']

/** Порядок значений справочника recipient_status (attribute_values.sort). */
const RECIPIENT_ORDER = [
  'Универсальный', 'Сын', 'Дочь', 'Внук', 'Внучка', 'Мама', 'Папа',
  'Бабушка', 'Дедушка', 'Муж', 'Жена', 'Брат', 'Сестра', 'Крёстная',
  'Крёстный', 'Любимый человек', 'Друг', 'Подруга', 'Учитель',
  'Воспитатель', 'Коллега', 'Руководитель', 'Семья', 'Пара',
]

/** Персоны по возрасту для одного пола. Унисекс = объединение мужской и женской. */
const PERSONAS_BY_AGE: Record<string, { male: string[]; female: string[] }> = {
  [AGE_BABY]: { male: ['Мальчик'], female: ['Девочка'] },
  [AGE_CHILD]: { male: ['Мальчик'], female: ['Девочка'] },
  [AGE_TEEN]: { male: ['Мальчик', 'Парень'], female: ['Девочка', 'Девушка'] },
  [AGE_ADULT]: { male: ['Парень', 'Мужчина'], female: ['Девушка', 'Женщина'] },
  [AGE_SENIOR]: { male: ['Мужчина'], female: ['Женщина'] },
  [AGE_UNIVERSAL]: {
    male: ['Мальчик', 'Парень', 'Мужчина'],
    female: ['Девочка', 'Девушка', 'Женщина'],
  },
}

/**
 * Статусы получателя по возрасту для одного пола.
 * «Универсальный» возраст означает товар без адресата — там и статус
 * «Универсальный», а не объединение всех родственных связей.
 */
const RECIPIENTS_BY_AGE: Record<string, { male: string[]; female: string[] }> = {
  [AGE_BABY]: { male: ['Сын', 'Внук'], female: ['Дочь', 'Внучка'] },
  [AGE_CHILD]: { male: ['Сын', 'Внук'], female: ['Дочь', 'Внучка'] },
  [AGE_TEEN]: { male: ['Сын', 'Внук'], female: ['Дочь', 'Внучка'] },
  [AGE_ADULT]: {
    male: ['Папа', 'Дедушка', 'Муж'],
    female: ['Мама', 'Бабушка', 'Жена'],
  },
  [AGE_SENIOR]: { male: ['Дедушка'], female: ['Бабушка'] },
  [AGE_UNIVERSAL]: { male: ['Универсальный'], female: ['Универсальный'] },
}

function orderBy(order: string[], values: string[]): string[] {
  const unique = [...new Set(values)]
  return unique.sort((left, right) => {
    const leftIndex = order.indexOf(left)
    const rightIndex = order.indexOf(right)
    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right, 'ru-RU')
    if (leftIndex === -1) return 1
    if (rightIndex === -1) return -1
    return leftIndex - rightIndex
  })
}

function derive(
  table: Record<string, { male: string[]; female: string[] }>,
  order: string[],
  gender: string,
  age: string,
): string[] {
  const row = table[String(age || '').trim()]
  if (!row) return []
  const normalizedGender = String(gender || '').trim()
  if (normalizedGender === GENDER_MALE) return orderBy(order, row.male)
  if (normalizedGender === GENDER_FEMALE) return orderBy(order, row.female)
  if (normalizedGender === GENDER_UNISEX) return orderBy(order, [...row.male, ...row.female])
  return []
}

/** Персоны для пары «пол × возраст». Пустой массив — пара не распознана. */
export function derivePersonas(gender: string, age: string): string[] {
  return derive(PERSONAS_BY_AGE, PERSONA_ORDER, gender, age)
}

/** Статусы получателя для пары «пол × возраст». Пустой массив — пара не распознана. */
export function deriveRecipientStatuses(gender: string, age: string): string[] {
  return derive(RECIPIENTS_BY_AGE, RECIPIENT_ORDER, gender, age)
}

/**
 * Оставляет только значения, которые есть в справочнике. Схема зафиксирована
 * в коде, а справочник живой: если значение выведено из архива, подставлять его
 * в форму нельзя — бэкенд отклонит запись по attribute-guard.
 */
export function keepKnownValues(values: string[], dictionary: string[] | undefined): string[] {
  if (!dictionary || dictionary.length === 0) return []
  const known = new Set(dictionary)
  return values.filter((value) => known.has(value))
}

export const IDEA_DICTIONARY_FIELDS = ['category', 'occasion', 'gender', 'age', 'persona', 'recipient_status', 'brand'] as const
export type IdeaCatalogDictionary = Record<string, string[]>
export interface IdeaCatalogFields {
  theme: string; brief: string; category: string; occasion: string; gender: string; age: string;
  brand_ru: string; personas: string[]; recipient_statuses: string[]; age_on_image: string | null;
  _meta: Record<string, any>;
}
export class IdeaCatalogValidationError extends Error {
  constructor(public readonly field: string, message: string) { super(message); this.name = 'IdeaCatalogValidationError' }
}
const FIELD_LABELS: Record<string, string> = {
  theme: 'Тематика', brief: 'Краткое ТЗ', category: 'Категория', occasion: 'Повод', gender: 'Пол аудитории',
  age: 'Возраст аудитории', brand_ru: 'Бренд', personas: 'Персоны', recipient_statuses: 'Статусы получателя', age_on_image: 'Возраст на изображении',
}
const canonicalKey = (value: string) => value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru')
export function canonicalIdeaAttribute(value: unknown, dictionary: string[] | undefined): string | null {
  if (typeof value !== 'string') return null
  const key = canonicalKey(value)
  if (!key) return ''
  return dictionary?.find(known => canonicalKey(known) === key) ?? null
}

/** One catalogue boundary for AI output and editable drafts; source brand is never inferred. */
export function normalizeIdeaCatalogFields(input: unknown, dictionary: IdeaCatalogDictionary, options: { strict?: boolean } = {}): { fields: IdeaCatalogFields; warnings: Record<string, string> } {
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new IdeaCatalogValidationError('fields', 'Атрибуты должны быть объектом')
  const source = input as Record<string, any>, warnings: Record<string, string> = {}
  const warn = (field: string, message: string) => {
    if (message.startsWith(`${field}:`) && FIELD_LABELS[field]) message = FIELD_LABELS[field] + message.slice(field.length)
    if (options.strict) throw new IdeaCatalogValidationError(field, message)
    warnings[field] = message
  }
  const text = (field: string, max: number): string => {
    const value = source[field]
    if (value == null || value === '') return ''
    if (typeof value !== 'string') { warn(field, `${field}: требуется строка`); return '' }
    const result = value.trim()
    if (result.length > max) warn(field, `${field}: не больше ${max} символов`)
    return result.slice(0, max)
  }
  const closed = (field: string): string => {
    if (source[field] == null || source[field] === '') return ''
    const result = canonicalIdeaAttribute(source[field], dictionary[field])
    if (result === null) { warn(field, `${field}: значение отсутствует в справочнике`); return '' }
    return result
  }
  const fields: IdeaCatalogFields = {
    theme: text('theme', 120), brief: text('brief', 1000), category: closed('category'), occasion: closed('occasion'),
    gender: closed('gender'), age: closed('age'), brand_ru: options.strict ? text('brand_ru', 255) : '', personas: [], recipient_statuses: [], age_on_image: null,
    _meta: source._meta && typeof source._meta === 'object' && !Array.isArray(source._meta) ? { ...source._meta } : {},
  }
  const multi = (field: 'personas' | 'recipient_statuses', key: string, defaults: string[]) => {
    if (!Object.prototype.hasOwnProperty.call(source, field)) return keepKnownValues(defaults, dictionary[key])
    if (!Array.isArray(source[field])) { warn(field, `${field}: требуется массив`); return [] }
    if (!options.strict && source[field].length === 0) return keepKnownValues(defaults, dictionary[key])
    if (source[field].length > 20) warn(field, `${field}: не больше 20 значений`)
    const result: string[] = []
    for (const value of source[field].slice(0, 20)) {
      const known = canonicalIdeaAttribute(value, dictionary[key])
      if (known === null || known === '') { warn(field, `${field}: значение отсутствует в справочнике`); continue }
      if (!result.includes(known)) result.push(known)
    }
    return result
  }
  fields.personas = multi('personas', 'persona', derivePersonas(fields.gender, fields.age))
  fields.recipient_statuses = multi('recipient_statuses', 'recipient_status', deriveRecipientStatuses(fields.gender, fields.age))
  if (source.age_on_image != null && source.age_on_image !== '') {
    if (typeof source.age_on_image !== 'string' || !/^\d{1,3}$/.test(source.age_on_image.trim()) || Number(source.age_on_image.trim()) > 150) warn('age_on_image', 'Возраст на изображении: числовая строка от 0 до 150')
    else fields.age_on_image = String(Number(source.age_on_image.trim()))
  }
  if (!options.strict) for (const field of ['theme', 'brief', 'category', 'occasion', 'gender', 'age'] as const) {
    if (!fields[field] && !warnings[field]) warn(field, `${field}: требуется проверка и заполнение`)
  }
  const reviewFields = [...new Set([...(Array.isArray(fields._meta.reviewFields) ? fields._meta.reviewFields.filter((v: unknown) => typeof v === 'string') : []), ...Object.keys(warnings)])]
  fields._meta.reviewFields = reviewFields
  fields._meta.fieldWarnings = { ...(fields._meta.fieldWarnings && typeof fields._meta.fieldWarnings === 'object' ? fields._meta.fieldWarnings : {}), ...warnings }
  return { fields, warnings }
}
